import React, { ChangeEvent, useRef, useState } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Input,
  Message,
  Select,
  Text,
  toast,
} from '@workwhile/ui'
import { Header } from 'components/Header'
import { useColumbiaTimesheetContextValue } from '../context/ColumbiaTimesheetProvider'
import { ColumbiaTimesheetDiffResults } from './ColumbiaTimesheetDiffResults'

type InstanceOption = {
  label: string
  value: number
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export const ColumbiaTimesheetMain = () => {
  const {
    state: { instances, diffMutationState, convertMutationState },
    actions: {
      compareTimesheet,
      resetCompare,
      generateTimesheetCsv,
      resetGenerate,
    },
  } = useColumbiaTimesheetContextValue()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [vndlyInstanceId, setVndlyInstanceId] = useState<number | null>(() => {
    const onlyInstance = instances.length === 1 ? instances[0] : undefined
    return onlyInstance?.id ?? null
  })

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const instanceOptions: InstanceOption[] = instances.map((instance) => ({
    label: instance.instanceUrl,
    value: instance.id,
  }))

  const selectedInstanceOption =
    instanceOptions.find((option) => option.value === vndlyInstanceId) ?? null

  const canCompare =
    file != null &&
    vndlyInstanceId != null &&
    startDate !== '' &&
    endDate !== ''

  const canGenerate = file != null && startDate !== '' && endDate !== ''
  const isBusy = diffMutationState.isPending || convertMutationState.isPending

  const resetResults = () => {
    resetCompare()
    resetGenerate()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    resetResults()
    setFile(event.target.files?.[0] ?? null)
  }

  const handleCompare = () => {
    if (
      file == null ||
      vndlyInstanceId == null ||
      startDate === '' ||
      endDate === ''
    ) {
      toast.error('Select an instance, date range, and timesheet CSV')
      return
    }

    resetCompare()
    compareTimesheet({
      vndlyInstanceId,
      startDate,
      endDate,
      timesheet: file,
    })
  }

  const handleGenerateCsv = () => {
    if (file == null || startDate === '' || endDate === '') {
      toast.error('Select a date range and timesheet CSV')
      return
    }

    resetGenerate()
    generateTimesheetCsv(
      {
        startDate,
        endDate,
        timesheet: file,
      },
      {
        onSuccess: ({ blob, filename }) => {
          downloadBlob(blob, filename)
          toast.success(`Downloaded ${filename}`)
        },
      }
    )
  }

  return (
    <>
      <Header title="Columbia Timesheet" />
      <Box margin={3}>
        <Text fontSize={2} color="lightText" mb={3}>
          Upload the Columbia-approved timesheet CSV to compare hours against
          WorkWhile, or convert it into VNDLY bulk upload format.
        </Text>

        <Box maxWidth={480}>
          <Field label="VNDLY instance">
            <Select
              options={instanceOptions}
              value={selectedInstanceOption}
              onChange={(option: InstanceOption | null) => {
                resetResults()
                setVndlyInstanceId(option?.value ?? null)
              }}
              placeholder="Select instance"
              isClearable={instances.length > 1}
            />
          </Field>

          <Flex gap={3} mt={3}>
            <Box flex={1}>
              <Field label="Start date">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    resetResults()
                    setStartDate(event.target.value)
                  }}
                />
              </Field>
            </Box>
            <Box flex={1}>
              <Field label="End date">
                <Input
                  type="date"
                  value={endDate}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    resetResults()
                    setEndDate(event.target.value)
                  }}
                />
              </Field>
            </Box>
          </Flex>

          <Box mt={3}>
            <Field label="Timesheet CSV">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
              />
            </Field>
          </Box>
          {file != null ? (
            <Text fontSize={1} color="lightText" mt={1}>
              Selected: {file.name}
            </Text>
          ) : null}

          <Flex gap={2} mt={3}>
            <Button
              variant="primary"
              onClick={handleCompare}
              loading={diffMutationState.isPending}
              disabled={!canCompare || isBusy}
            >
              Compare with WorkWhile
            </Button>
            <Button
              variant="secondary"
              onClick={handleGenerateCsv}
              loading={convertMutationState.isPending}
              disabled={!canGenerate || isBusy}
            >
              Generate VNDLY upload CSV
            </Button>
          </Flex>
        </Box>

        {diffMutationState.isError && diffMutationState.errorMessage ? (
          <Message variant="error" mt={3}>
            {diffMutationState.errorMessage}
          </Message>
        ) : null}

        {convertMutationState.isError && convertMutationState.errorMessage ? (
          <Message variant="error" mt={3}>
            {convertMutationState.errorMessage}
          </Message>
        ) : null}

        <ColumbiaTimesheetDiffResults />
      </Box>
    </>
  )
}
